/**
 * This file is responsible for rendering the contact us page in the application.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the page content, disclaimer text, and translations for the page.
 * The page content and disclaimer text are fetched using the GetCmsBlocks GraphQL query.
 *
 *
 *
 * The ContactUs component from '@Hotels-at-Home/hah-fe-core' is used to render the contact us form.
 *
 * If there's an error during server-side rendering or fetching the page content or disclaimer text, it is passed as a prop to the component and can be used to display an error message.
 *
 * The NextPage component from '~/ui/layouts/NextPage' is used to render the page layout.
 */

import React, { ReactElement } from 'react';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import ContactUs from '@Hotels-at-Home/hah-fe-core/dist/components/Help/ContactUs';
import { getLocalNavigationItems } from '../../../constants/helpNavigation';

import { publicQuery, GetCmsBlocks } from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';
import { extractErrorMessage } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';

import { TServerSideProps } from '@Hotels-at-Home/hah-fe-core/dist/types';
import apolloConfig from '~/constants/apolloConfig';
import { LanguageCode } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { usePageViewEvent } from '@Hotels-at-Home/hah-fe-core/dist/hooks';

interface IContactUs {
  pageContent: string;
  disclaimerText: string;
}

type TProps = TServerSideProps<IContactUs>;

const ContactUsPage = (props: TProps): ReactElement => {
  const { error } = props;

  const { t } = useTranslation('', { keyPrefix: '' });

  usePageViewEvent();
  const meta = {
    title: `${t('$local.$meta.meta_title')} | ${t('$common.$headings.contactus')}`,
    description: `${t('$local.$meta.meta_title')} | ${t('$common.$headings.contactus')}`,
  };

  return (
    <NextPage error={error} meta={meta}>
      <ContactUs {...props} getLocalNavigationItems={getLocalNavigationItems} />
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps<TProps> = async ctx => {
  const props: TProps = {
    ...(await serverSideTranslations(ctx.locale)),
    error: null,
    pageContent: null,
    disclaimerText: null,
  };

  try {
    props.pageContent = await publicQuery(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: GetCmsBlocks,
        variables: {
          locale: ctx.locale,
          identifiers: ['contact-us'],
        },
      },
      'GetCmsBlocks',
      ctx,
    ).then(res => res.data.cmsBlocks.items[0]?.content);
  } catch (e) {
    props.error = extractErrorMessage(e);
  }

  try {
    props.disclaimerText = await publicQuery(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: GetCmsBlocks,
        variables: {
          locale: ctx.locale,
          identifiers: ['contact-us-disclaimer'],
        },
      },
      'GetCmsBlocks',
      ctx,
    ).then(response => response.data.cmsBlocks.items[0]?.content);
  } catch (e) {
    props.error = extractErrorMessage(e);
  }

  return { props };
};

export default ContactUsPage;
